import { Link, createFileRoute } from "@tanstack/react-router";
import { PageImageGallery } from "@/components/page-image-gallery";
import { useMemo, useState } from "react";

import { ProseSection } from "@/components/prose-section";
import { DepositSpeedInfographic, TestCoverageInfographic } from "@/components/infographics";
import { CasinoList } from "@/components/casino-list";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { formatEuro } from "@/lib/format";
import { getPublicCasinos, getPublishedMethods } from "@/lib/queries";
import { SITE } from "@/lib/site";
import { ogImageMeta } from "@/lib/og";

type Search = { methode?: string | undefined; opnemen?: string | undefined; kosten?: string | undefined };

export const Route = createFileRoute("/casinozoeker")({
  validateSearch: (search: Record<string, unknown>): Search => ({
    methode: typeof search["methode"] === "string" ? (search["methode"] as string) : undefined,
    opnemen: typeof search["opnemen"] === "string" ? (search["opnemen"] as string) : undefined,
    kosten: typeof search["kosten"] === "string" ? (search["kosten"] as string) : undefined,
  }),
  head: ({ match }) => {
    const search = (match?.search ?? {}) as Search;
    // Elke filtercombinatie is een zoekresultaat: altijd noindex, wel follow.
    const hasFilters = Object.values(search).some((v) => v !== undefined);
    return {
      meta: [
        { title: "Casinozoeker 2026: Filter op Betaalmethode, Limiet en Kosten" },
        {
          name: "description",
          content:
            "Vind jouw beste online casino uit onze top 10. Filter op betaalmethode, lage storting, snelle uitbetaling en kosten. Getest in november 2026. 18+.",
        },
        {
          name: "robots",
          content: hasFilters ? "noindex,follow" : "index, follow, max-image-preview:large",
        },
        { property: "og:title", content: "Casinozoeker 2026: Filter op Betaalmethode, Limiet en Kosten" },
        {
          property: "og:description",
          content: "Vind de beste casino's op betaalmethode, minimale storting, uitbetaling en kosten. November 2026, 18+.",
        },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        ...ogImageMeta("casinozoeker"),
      ],
      // canonical zonder filterparameters: alle filtercombinaties wijzen naar de basis-URL
      links: [{ rel: "canonical", href: `${SITE.url}/casinozoeker/` }],
    };
  },
  component: CasinoFinderPage,
});

function CasinoFinderPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const methods = getPublishedMethods();
  const casinos = getPublicCasinos();

  const [method, setMethod] = useState<string>(search.methode ?? "alle");
  const [needsWithdrawal, setNeedsWithdrawal] = useState<boolean>(search.opnemen === "1");
  const [noFees, setNoFees] = useState<boolean>(search.kosten === "geen");
  const [maxDeposit, setMaxDeposit] = useState<number>(50);

  const updateSearch = (next: Partial<Search>) => {
    void navigate({
      search: (prev: Search) => ({ ...prev, ...next }),
      replace: true,
    });
  };

  const results = useMemo(() => {
    return casinos.filter((casino) => {
      if (casino.minimum_deposit !== null && casino.minimum_deposit > maxDeposit) return false;
      if (method !== "alle") {
        const link = casino.links.find((l) => l.method?.slug === method);
        if (!link) return false;
        if (needsWithdrawal && !link.withdrawal_supported) return false;
        if (noFees && !link.no_known_fees) return false;
      } else if (needsWithdrawal) {
        if (!casino.links.some((l) => l.withdrawal_supported)) return false;
      }
      if (noFees && method === "alle") {
        if (!casino.links.some((l) => l.no_known_fees)) return false;
      }
      return true;
    });
  }, [casinos, method, needsWithdrawal, noFees, maxDeposit]);

  return (
    <main className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
      <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">Casinozoeker: online casino vergelijken op betaalmethode</h1>
      <p className="mt-3 max-w-2xl text-muted-foreground">
        Vind de beste optie op basis van de betaalgegevens die voor jou tellen. Alle resultaten komen uit onze vaste top 10 en zijn in november 2026 opnieuw gecontroleerd.
      </p>

      <div className="mt-8 grid gap-8 lg:grid-cols-[280px_1fr]">
        <aside className="rounded-lg border border-border bg-card p-5 lg:sticky lg:top-32 lg:self-start">
          <p className="text-sm font-semibold text-foreground">Filters</p>

          <div className="mt-5 space-y-6">
            <div className="space-y-2">
              <Label htmlFor="methode">Betaalmethode</Label>
              <Select
                value={method}
                onValueChange={(value) => {
                  setMethod(value);
                  updateSearch({ methode: value === "alle" ? undefined : value });
                }}
              >
                <SelectTrigger id="methode">
                  <SelectValue placeholder="Kies een methode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="alle">Alle methoden</SelectItem>
                  {methods.map((m) => (
                    <SelectItem key={m.id} value={m.slug}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <Label htmlFor="storting">Maximale minimale storting</Label>
              <Slider
                id="storting"
                min={1}
                max={50}
                step={1}
                value={[maxDeposit]}
                onValueChange={(values) => setMaxDeposit(values[0] ?? 50)}
              />
              <p className="text-xs text-muted-foreground">
                Toon casino's met storting vanaf {formatEuro(1)} t/m {formatEuro(maxDeposit)}
              </p>
            </div>

            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="opnemen" className="text-sm font-normal">
                Opnemen via dezelfde methode
              </Label>
              <Switch
                id="opnemen"
                checked={needsWithdrawal}
                onCheckedChange={(checked) => {
                  setNeedsWithdrawal(checked);
                  updateSearch({ opnemen: checked ? "1" : undefined });
                }}
              />
            </div>

            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="kosten" className="text-sm font-normal">
                Geen transactiekosten
              </Label>
              <Switch
                id="kosten"
                checked={noFees}
                onCheckedChange={(checked) => {
                  setNoFees(checked);
                  updateSearch({ kosten: checked ? "geen" : undefined });
                }}
              />
            </div>
          </div>
        </aside>

        <section aria-live="polite" aria-label="Resultaten">
          <h2 className="mb-3 text-lg font-semibold text-foreground">Top 10 casino&apos;s vergelijken op betaalmethode</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            {results.length} aanbieder(s) gevonden
          </p>
          <CasinoList casinos={results} />
        </section>
      </div>

      <div className="mt-12 grid gap-6 lg:grid-cols-2">
        <DepositSpeedInfographic />
        <TestCoverageInfographic />
      </div>

      <ProseSection id="filters-uitleg" title="Wat de filters betekenen">
        <p>
          Het filter op minimale storting bepaalt hoe klein je eerste inleg mag zijn. Bij een lage
          drempel test je een aanbieder eerst met een klein bedrag; dat werkt het prettigst met{" "}
          <Link to="/betaalmethoden/$slug/" params={{ slug: "ideal" }}>iDEAL</Link>, omdat de storting
          direct wordt bijgeschreven en je niets hoeft op te waarderen.
        </p>
        <p>
          Zet je "opnemen via dezelfde methode" aan, dan blijven alleen combinaties over waarbij je
          winst terugkomt op de rekening of wallet waarmee je stortte. Dat geldt onder meer voor{" "}
          <Link to="/betaalmethoden/$slug/" params={{ slug: "trustly" }}>Trustly</Link>,{" "}
          <Link to="/betaalmethoden/$slug/" params={{ slug: "skrill" }}>Skrill</Link> en{" "}
          <Link to="/betaalmethoden/$slug/" params={{ slug: "paypal" }}>PayPal</Link>. Prepaid opties
          zoals <Link to="/betaalmethoden/$slug/" params={{ slug: "paysafecard" }}>Paysafecard</Link>{" "}
          vallen dan af, omdat een uitbetaling daar altijd via je bank loopt.
        </p>
        <p>
          Wil je eerst de meetgegevens per methode zien, bekijk dan{" "}
          <Link to="/betaalmethoden/">alle betaalmethoden</Link>. Elke pagina bevat het volledige
          testrapport, de kostenstructuur en de verantwoording van{" "}
          <Link to="/auteurs/">onze redactie</Link>.
        </p>
      </ProseSection>

          <PageImageGallery path="/casinozoeker" title="Vergelijken op betaalmethode" />
    </main>
  );
}
